import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaQuoteLeft, FaStar } from 'react-icons/fa';

const data = [
  {
    name: 'R. Sharma',
    role: 'Principal',
    quote:
      'Skhool helped us reduce admin workload by over 50%. Teachers now focus on learning outcomes instead of chasing manual tasks.',
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=120&q=80',
    accent: 'linear-gradient(145deg, rgba(59,130,246,0.16), rgba(147,197,253,0.08))',
  },
  {
    name: 'K. Sundar Chowdary',
    role: 'Head Teacher',
    quote:
      'Parent communication became instant. Attendance, homework, and exam workflows feel connected instead of scattered.',
    image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=120&q=80',
    accent: 'linear-gradient(145deg, rgba(249,115,22,0.16), rgba(253,186,116,0.08))',
  },
  {
    name: 'S. Agrawal',
    role: 'School Admin',
    quote:
      'Fees, transport, and student records are all connected. Reports that took hours now take minutes with far less confusion.',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=120&q=80',
    accent: 'linear-gradient(145deg, rgba(244,63,94,0.16), rgba(251,113,133,0.08))',
  },
];

const Section = styled.section`
  max-width: 1220px;
  margin: 0 auto;
  padding: 1rem 1.25rem 3.4rem;
`;

const Shell = styled.div`
  border-radius: 34px;
  padding: 2rem 1.1rem;
  background:
    radial-gradient(circle at 0% 0%, rgba(59, 130, 246, 0.08), transparent 28%),
    radial-gradient(circle at 100% 0%, rgba(251, 113, 133, 0.08), transparent 28%),
    linear-gradient(180deg, #ffffff 0%, #fdfcff 100%);
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 24px 54px rgba(15, 23, 42, 0.08);

  @media (min-width: 768px) {
    padding: 2.4rem 1.5rem 2.1rem;
  }
`;

const Header = styled.div`
  text-align: center;
  max-width: 760px;
  margin: 0 auto 1.8rem;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.82rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.04);
  color: #2563eb;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const Title = styled.h2`
  margin: 0.95rem 0 0;
  font-size: clamp(1.8rem, 3vw, 2.75rem);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.03em;
  color: #0f172a;
`;

const Description = styled.p`
  margin: 0.7rem auto 0;
  max-width: 640px;
  color: #475569;
  line-height: 1.7;
  font-size: 0.98rem;
`;

const Grid = styled.div`
  display: grid;
  gap: 1rem;

  @media (min-width: 900px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const Card = styled(motion.div)`
  position: relative;
  overflow: hidden;
  padding: 1.35rem;
  border-radius: 26px;
  background: ${({ $accent }) => $accent};
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.08);
  min-height: 100%;
`;

const QuoteIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.78);
  color: #0f172a;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
`;

const Quote = styled.p`
  margin: 1rem 0 1.25rem;
  color: #1e293b;
  line-height: 1.75;
  font-size: 0.95rem;
  min-height: 132px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
`;

const Profile = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const Avatar = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.9);
`;

const Name = styled.div`
  font-weight: 800;
  color: #0f172a;
`;

const Role = styled.div`
  font-size: 0.82rem;
  color: #475569;
  margin-top: 0.16rem;
`;

const Stars = styled.div`
  display: inline-flex;
  gap: 0.2rem;
  color: #f59e0b;
  flex-shrink: 0;
`;

const Testimonials = () => {
  return (
    <Section id="testimonials">
      <Shell>
        <Header>
          <Eyebrow>
            <FaStar />
            School Voices
          </Eyebrow>
          <Title>Trusted by schools that want cleaner systems and calmer daily operations</Title>
          <Description>
            Real feedback from leaders using Skhool to streamline attendance, communication, finance, transport, and reporting.
          </Description>
        </Header>

        <Grid>
          {data.map((item, index) => (
            <Card
              key={item.name}
              $accent={item.accent}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.28 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              whileHover={{ y: -5 }}
            >
              <QuoteIcon>
                <FaQuoteLeft />
              </QuoteIcon>
              <Quote>“{item.quote}”</Quote>
              <Footer>
                <Profile>
                  <Avatar src={item.image} alt={item.name} />
                  <div>
                    <Name>{item.name}</Name>
                    <Role>{item.role}</Role>
                  </div>
                </Profile>
                <Stars>
                  <FaStar />
                  <FaStar />
                  <FaStar />
                </Stars>
              </Footer>
            </Card>
          ))}
        </Grid>
      </Shell>
    </Section>
  );
};

export default Testimonials;
